import { useContext, JSX, useEffect } from "react";
import { withRouter, Router } from "next/router";
import Link from "next/link";
import { signIn } from "src/utils/nextAuth";
import { AuthProvider } from "pages/api/auth/getUserTokenFromApi";
import { SocialButton } from "src/atoms/SocialButton";
import { t, Trans } from "@lingui/macro";
import { Heading, Text } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { Container } from "src/atoms/Container";
import { AppCtx } from "src/contexts/app.context";
import { googleTagManager } from "src/tracking";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { PageHead } from "src/molecules/PageHead";
import { useLingui } from "@lingui/react";
import {
  getStorageJSON,
  removeStorageItem,
  setStorageItem,
  setStorageJSON,
} from "src/utils/helper/localStorage";
import {
  GATED_POPUP_REDIRECT_DATA,
  GATED_POPUP_SOURCE,
  GATED_REDIRECT_URL,
} from "src/constants/gatedItem";
import NoSSR from "src/atoms/NoSSR";
import { getQueryString } from "src/modules/page/utils";

const LoginView = ({ router }: { router: Router }): JSX.Element => {
  const { i18n } = useLingui();
  const { locale } = useContext(AppCtx);
  const { query } = router;

  const queryString = getQueryString(query);

  const { redirect_to, source } = query;

  if (source === GATED_POPUP_SOURCE && redirect_to) {
    setStorageItem(GATED_REDIRECT_URL, redirect_to as string);
  }

  const signInUrl = queryString ? `/sign-in/?${queryString}` : "/sign-in/";

  const handleLoginClick = () => {
    const gatedRedirectStorage: { gatedActionType?: string } =
      getStorageJSON(GATED_POPUP_REDIRECT_DATA) || {};

    if (gatedRedirectStorage.gatedActionType) {
      gatedRedirectStorage.gatedActionType = "Login";

      setStorageJSON(GATED_POPUP_REDIRECT_DATA, gatedRedirectStorage);
    }
  };

  useEffect(() => {
    if (query?.source !== GATED_POPUP_SOURCE) {
      removeStorageItem(GATED_POPUP_REDIRECT_DATA);
    }
  }, [query]);

  return (
    <Container mt={["48px", "48px", "48px", 6]} px={5}>
      <PageHead title={t(i18n)`Sign up | Otrium`} />
      <NoSSR>
        <Box
          width={["100%", "100%", "100%", "750px"]}
          sx={{ textAlign: "center", margin: "0 auto" }}
        >
          <Heading
            my={2}
            as="h2"
            fontFamily="PlusJakartaSans"
            fontSize={["40px", "40px", "40px", "48px"]}
            letterSpacing="normal"
            lineHeight={["1.2", "1.2", "1.2", "1.33"]}
            sx={{
              textTransform: "none",
            }}
          >
            <Trans>Register</Trans>
          </Heading>
          <Text
            mt={3}
            mb={[3, 3, 3, 5]}
            fontSize="18px"
            letterSpacing="0.8px"
            lineHeight="1.56"
          >
            <Trans>
              Get free access to hundreds of designer outlet stores.
            </Trans>
          </Text>
          <Flex justifyContent="center" alignItems="center">
            <Box width={["100%", "100%", "350px", "352px"]}>
              <SocialButton
                data-testid="register-view-register-by-facebook-button"
                fullWidth
                type="facebook"
                onClick={() =>
                  void signIn(AuthProvider.FACEBOOK, {
                    assumedAuthType: "sign_up",
                  })
                }
              >
                Facebook
              </SocialButton>
              <SocialButton
                data-testid="register-view-register-by-google-button"
                fullWidth
                type="google"
                onClick={() =>
                  void signIn(AuthProvider.GOOGLE, {
                    assumedAuthType: "sign_up",
                  })
                }
              >
                Google
              </SocialButton>
              <SocialButton
                data-testid="register-view-register-by-email-button"
                fullWidth
                type="email"
                onClick={() =>
                  void router.push(
                    `/register-email${queryString ? `?${queryString}` : ""}`
                  )
                }
              >
                Email
              </SocialButton>
              <Trans>
                <Text
                  mt={2}
                  fontSize={0}
                  color="tone.anthraciteLight"
                  letterSpacing="normal"
                  lineHeight="1.43"
                >
                  By signing up, you agree with our{" "}
                  <a
                    href={getI18nRoute({
                      route: "/terms-and-conditions",
                      locale,
                    })}
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    Terms & Conditions
                  </a>
                </Text>
              </Trans>
            </Box>
          </Flex>
          <Flex
            mt={[5, 5, 5, "48px"]}
            mb={[6, 6, 6, "96px"]}
            justifyContent="center"
            alignItems="center"
          >
            <Text
              color="Black"
              fontSize={1}
              letterSpacing="0.4px"
              lineHeight="1.5"
            >
              <Trans>Already registered? </Trans>
            </Text>
            <Link href={signInUrl} passHref onClick={handleLoginClick}>
              <Text
                as="a"
                pl={1}
                color="Black"
                fontWeight="600"
                sx={{
                  textDecoration: "underline",
                  ":hover": { cursor: "pointer" },
                }}
                onClick={() => googleTagManager.trackAlreadyRegistered()}
              >
                <Trans>Log in</Trans>
              </Text>
            </Link>
          </Flex>
        </Box>
      </NoSSR>
    </Container>
  );
};
export default withRouter(LoginView);
